import {
    useAPIQuery,
    useGlobalAPIQueryContext,
} from "@dev2_techwalnut/tw-api-query-hook";
import { zodResolver } from "@hookform/resolvers/zod";
import moment from "moment";
import { useEffect, useRef } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import secureLocalStorage from "react-secure-storage";
import Select from "react-select";
import { z } from "zod";

const createSchema = (targetQuantity) => {
    return z.object({
        work_orders: z
            .array(
                z
                    .object({
                        work_order_id: z.object(
                            {
                                value: z.union([z.string(), z.number()]),
                                label: z.string(),
                                available_stock: z.number().optional(), // Add available_stock to schema
                            },
                            { message: "Work order is required" }
                        ),
                        allocated_quantity: z
                            .number({
                                required_error: "Alloting quantity is required",
                                invalid_type_error:
                                    "Alloting quantity is required",
                            })
                            .min(0, `Alloting quantity cannot be negative`),
                    })
                    .superRefine((workOrder, ctx) => {
                        const availableStock =
                            workOrder.work_order_id?.available_stock;
                        const allocatedQty = workOrder.allocated_quantity;

                        if (
                            availableStock === undefined ||
                            targetQuantity === undefined
                        ) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Available stock or target quantity is not provied`,
                                path: ["allocated_quantity"],
                            });
                        }

                        console.log(allocatedQty);
                        console.log(availableStock);

                        if (allocatedQty > availableStock) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Allocated quantity (${allocatedQty}) cannot exceed available stock (${availableStock})`,
                                path: ["allocated_quantity"],
                            });
                        }

                        if (allocatedQty > targetQuantity) {
                            ctx.addIssue({
                                code: z.ZodIssueCode.custom,
                                message: `Allocated quantity (${allocatedQty}) cannot exceed requested quantity (${targetQuantity})`,
                                path: ["allocated_quantity"],
                            });
                        }
                    })
            )
            .superRefine((workOrders, ctx) => {
                // Validate total allocated quantity against target quantity
                const total = workOrders.reduce(
                    (sum, wo) => sum + (wo.allocated_quantity || 0),
                    0
                );

                if (total > targetQuantity) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `Alloted quantity (${total}) cannot exceed requested quantity: (${targetQuantity}).`,
                        path: [],
                    });
                }
            }),
    });
};

const AllotWIP = ({
    disabled,
    targetQuantity,
    wipId,
    itemId,
    salesorderId,
    workOrderId,
    setRecheckBom,
}) => {
    const userID = secureLocalStorage.getItem("userID");
    const {
        register,
        handleSubmit,
        formState: { errors, isValid },
        control,
        setValue,
        watch,
        reset,
        trigger,
    } = useForm({
        mode: "onBlur",
        resolver: zodResolver(createSchema(targetQuantity ?? 0)),
    });

    const backButtonRef = useRef(null);

    const goBackHandler = () => {
        if (backButtonRef.current) {
            backButtonRef.current.click();
        }
    };

    const { invalidateApi } = useGlobalAPIQueryContext();

    const { useFetch: useFetchBatches } = useAPIQuery({
        key: "api/batch",
        endpoint: "api/batch",
    });
    const { useCreate: createAllotWIP } = useAPIQuery({
        key: "updateBOM",
        endpoint: `/api/allot-wip-rm/`,
    });

    const { data: batches, isLoading: loadingBatches } = useFetchBatches({
        params: {
            items: itemId,
        },
        abortOnNewRequest: true,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 3,
        retry: 3,
        keepPlaceholder: true,
        refetchOnReconnect: true,
        enabled: !!itemId,
    });

    const { mutate: mutateAllotWIP, isPending } = createAllotWIP({
        successMessageFromServer: true,
        errorMessageFromServer: true,
        onSuccess: () => {
            setRecheckBom(true);
            reset({ work_orders: [] });
            invalidateApi({ key: "api/batch" });
            const button = document.createElement("button");
            button.className = "d-none";
            button.setAttribute("data-bs-toggle", "modal");
            button.setAttribute("data-bs-target", `#check-bom-${workOrderId}`);
            document.body.appendChild(button);
            button.click();
            button.remove();
        },
        // onError: handleAllotWIPError,
        retry: 2,
    });

    const { fields, append, remove } = useFieldArray({
        name: "work_orders",
        control,
    });

    const watchedBatches = watch(`work_orders`);

    const total_alloted_quantity = watch("work_orders")?.reduce(
        (sum, wo) => sum + (wo?.allocated_quantity || 0),
        0
    );

    const onSubmit = (data) => {
        console.log(data);
    };

    const handleAllocate = () => {
        const wos = watch("work_orders");

        console.log(wos);

        const total = wos?.reduce(
            (sum, wo) => sum + (wo?.allocated_quantity || 0),
            0
        );

        console.log(total);

        // const res = wos?.map((wo) => ({
        //     id: wipId,
        //     allotted_stock: wo?.allocated_quantity,
        //     is_stock_allotted: true,
        // }));

        const res = {
            work_order_id: workOrderId,
            sales_order_id: salesorderId,
            bulk_upload: [
                {
                    id: wipId,
                    batches: wos?.map((wo) => ({
                        batch_no: wo?.work_order_id?.value,
                        received_quantity: wo?.allocated_quantity,
                    })),
                    allotted_stock: Number(total),
                    is_stock_allotted: true,
                    updated_by: userID,
                },
            ],
            // is_locked: true,
            created_by: userID,
            updated_by: userID,
        };

        console.log(res);

        mutateAllotWIP({
            data: res,
        });
    };

    useEffect(() => {
        return () => {
            const backdrop = document.querySelector(".modal-backdrop");
            if (backdrop) {
                backdrop.remove();
                document.body.classList.remove("modal-open");
                document.body.style = "";
            }
        };
    }, []);

    return (
        <>
            <div
                className="modal fade m-0 p-0"
                data-bs-backdrop="static"
                data-bs-keyboard="false"
                tabIndex={-1}
                id={`allocate-wip`}
                aria-modal
            >
                <div className="modal-dialog modal-lg" role="document">
                    <div className="modal-content">
                        <button
                            type="button"
                            className="close"
                            data-bs-dismiss="modal"
                        >
                            <em
                                className="icon ni ni-cross"
                                aria-hidden="true"
                            ></em>
                        </button>
                        <div className="modal-header">
                            <h5 className="modal-title">Allocate</h5>
                        </div>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="modal-body">
                                <div className="row g-3 mb-3">
                                    <div className="col-4">
                                        <p>
                                            <strong className="me-2">
                                                Requested Quantity:
                                            </strong>
                                            {targetQuantity}
                                        </p>
                                    </div>
                                    <div className="col-4">
                                        <p>
                                            <strong className="me-2">
                                                Alloted Quantity:
                                            </strong>
                                            {total_alloted_quantity}
                                        </p>
                                        {errors?.work_orders && (
                                            <span className="fs-8 text-danger">
                                                {
                                                    errors?.work_orders?.root
                                                        ?.message
                                                }
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {fields?.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="table table-bordered mb-3">
                                            <thead>
                                                <tr>
                                                    <th
                                                        style={{ width: "40%" }}
                                                    >
                                                        Work Order
                                                        <small className="text-danger ms-1">
                                                            *
                                                        </small>
                                                    </th>
                                                    <th
                                                        style={{ width: "40%" }}
                                                    >
                                                        Quantity
                                                        <small className="text-danger ms-1">
                                                            *
                                                        </small>
                                                    </th>
                                                    <th
                                                        style={{ width: "20%" }}
                                                    >
                                                        Remove
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {fields?.map((field, i) => (
                                                    <tr key={field.id}>
                                                        <td>
                                                            <Controller
                                                                name={`work_orders.${i}.work_order_id`}
                                                                control={
                                                                    control
                                                                }
                                                                render={({
                                                                    field,
                                                                }) => {
                                                                    const selectedBatches =
                                                                        watchedBatches
                                                                            .filter(
                                                                                (
                                                                                    _,
                                                                                    idx
                                                                                ) =>
                                                                                    idx !==
                                                                                    i
                                                                            )
                                                                            .map(
                                                                                (
                                                                                    f
                                                                                ) =>
                                                                                    f
                                                                                        .work_order_id
                                                                                        ?.label
                                                                            )
                                                                            .filter(
                                                                                Boolean
                                                                            );

                                                                    const availableOptions =
                                                                        // batches?.results?.data?.[
                                                                        //     itemId
                                                                        // ]
                                                                        Object.values(
                                                                            batches
                                                                                ?.results
                                                                                ?.data ??
                                                                                {}
                                                                        )
                                                                            ?.flat()
                                                                            ?.map(
                                                                                (
                                                                                    b
                                                                                ) => ({
                                                                                    available_stock:
                                                                                        b?.received_quantity,
                                                                                    value: b?.batch_number,
                                                                                    label: `${
                                                                                        b?.batch_number
                                                                                    }:${
                                                                                        b?.created_at &&
                                                                                        moment(
                                                                                            b?.created_at
                                                                                        ).isValid()
                                                                                            ? moment(
                                                                                                  b.created_at
                                                                                              ).format(
                                                                                                  "DD-MM-YYYY"
                                                                                              )
                                                                                            : null
                                                                                    }`,
                                                                                })
                                                                            )
                                                                            .filter(
                                                                                (
                                                                                    option
                                                                                ) =>
                                                                                    !selectedBatches.includes(
                                                                                        option.label
                                                                                    )
                                                                            ) ||
                                                                        [];
                                                                    return (
                                                                        <Select
                                                                            {...field}
                                                                            options={
                                                                                availableOptions
                                                                            }
                                                                            isLoading={
                                                                                loadingBatches
                                                                            }
                                                                            styles={{
                                                                                menuPortal:
                                                                                    (
                                                                                        base
                                                                                    ) => ({
                                                                                        ...base,
                                                                                        zIndex: 9999,
                                                                                    }),
                                                                                menu: (
                                                                                    base
                                                                                ) => ({
                                                                                    ...base,
                                                                                    zIndex: 9999,
                                                                                }),
                                                                            }}
                                                                            menuPortalTarget={
                                                                                document.body
                                                                            }
                                                                            placeholder={`Select a work order`}
                                                                            onChange={(
                                                                                val
                                                                            ) => {
                                                                                field.onChange(
                                                                                    val
                                                                                );
                                                                                setValue(
                                                                                    `work_orders.${i}.allocated_quantity`,
                                                                                    val?.available_stock,
                                                                                    {
                                                                                        shouldValidate: true,
                                                                                        shouldDirty: true,
                                                                                    }
                                                                                );
                                                                            }}
                                                                        />
                                                                    );
                                                                }}
                                                            />

                                                            {errors
                                                                ?.work_orders?.[
                                                                i
                                                            ]?.work_order_id
                                                                ?.message && (
                                                                <span className="fs-8 text-danger">
                                                                    {
                                                                        errors
                                                                            ?.work_orders?.[
                                                                            i
                                                                        ]
                                                                            ?.work_order_id
                                                                            ?.message
                                                                    }
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                {...register(
                                                                    `work_orders.${i}.allocated_quantity`,
                                                                    {
                                                                        valueAsNumber: true,
                                                                    }
                                                                )}
                                                                disabled={
                                                                    !watch(
                                                                        `work_orders.${i}.work_order_id`
                                                                    )
                                                                }
                                                                onBlur={() =>
                                                                    trigger()
                                                                }
                                                            />
                                                            {errors
                                                                ?.work_orders?.[
                                                                i
                                                            ]
                                                                ?.allocated_quantity
                                                                ?.message && (
                                                                <span className="fs-8 text-danger">
                                                                    {
                                                                        errors
                                                                            ?.work_orders?.[
                                                                            i
                                                                        ]
                                                                            ?.allocated_quantity
                                                                            ?.message
                                                                    }
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-danger"
                                                                onClick={() =>
                                                                    remove(i)
                                                                }
                                                            >
                                                                Remove
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : null}
                                <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={() =>
                                        append({
                                            work_order_id: null,
                                            allocated_quantity: 0,
                                        })
                                    }
                                >
                                    + Allocate
                                </button>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    data-bs-toggle="modal"
                                    data-bs-target={`#check-bom-${workOrderId}`}
                                    onClick={() => reset({ work_orders: [] })}
                                    disabled={isPending}
                                    ref={backButtonRef}
                                >
                                    Back
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    disabled={
                                        !isValid ||
                                        disabled ||
                                        isPending ||
                                        fields?.length <= 0
                                    }
                                    onClick={() => handleAllocate()}
                                >
                                    {isPending ? "Submitting..." : "Submit"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AllotWIP;
